/*
 * Collection grid: loads the next page of products into the grid as the
 * shopper nears the bottom. The server still renders ordinary numbered pages
 * (the links stay in the page for search engines and for shoppers without
 * scripts); this only appends them.
 *
 * Back button: Shopify tells browsers not to keep a copy of these pages, so a
 * "back" always reloads them. To make that feel like a kept page, we save the
 * extra cards and the scroll position when the shopper leaves (per tab,
 * sessionStorage) and put them straight back on return. If the saved copy is
 * missing or older than ten minutes, the batches are loaded again instead.
 */
(function () {
  if (window.__sbfaInfiniteInit) return;
  window.__sbfaInfiniteInit = true;

  const root = document.querySelector('[data-infinite-scroll]');
  const grid = document.getElementById('product-grid');
  if (!root || !grid) return;

  const status = root.querySelector('[data-infinite-status]');
  const button = root.querySelector('[data-infinite-more]');
  const pagination = document.querySelector('[data-infinite-pagination]');
  const sectionId = root.dataset.sectionId;
  const STATE_KEY = 'sbfaInfinite:' + window.location.pathname + window.location.search;
  const STATE_MAX_AGE = 6 * 60 * 60 * 1000;
  const SNAPSHOT_MAX_AGE = 10 * 60 * 1000;

  const pageOf = (url) => Number(new URL(url, window.location.origin).searchParams.get('page')) || 1;

  let nextUrl = root.dataset.nextUrl;
  let lastPage = pageOf(window.location.href);
  const startPage = lastPage;
  let loading = false;
  let observer = null;

  // One link per card tells us whether a piece is already on the page, so a
  // collection that is reordered mid-scroll never shows the same piece twice.
  const cardKey = (li) => {
    const a = li.querySelector('a[href*="/products/"]');
    return a ? a.getAttribute('href').split('?')[0] : null;
  };
  const seen = new Set([...grid.children].map(cardKey).filter(Boolean));
  const initialCount = grid.children.length;

  if (pagination) pagination.hidden = true;

  function readState() {
    try {
      const state = JSON.parse(sessionStorage.getItem(STATE_KEY));
      if (state && Date.now() - state.t < STATE_MAX_AGE) return state;
    } catch (error) {}
    return null;
  }

  function saveState() {
    const state = {
      lastPage,
      nextUrl,
      scrollY: window.scrollY,
      gridHeight: grid.offsetHeight,
      t: Date.now(),
      html: [...grid.children].slice(initialCount).map((li) => li.outerHTML).join(''),
    };
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (error) {
      // Too big for the tab's storage: keep the position and reload the batches on return.
      try {
        state.html = '';
        sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
      } catch (ignored) {}
    }
  }

  window.addEventListener('pagehide', saveState);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveState();
  });

  function finish() {
    nextUrl = '';
    if (observer) observer.disconnect();
    root.hidden = true;
  }

  // Fetches one numbered page as a bare section and returns its cards and the
  // link to the page after it.
  async function fetchBatch(url) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error('HTTP ' + response.status);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = doc.querySelector('[data-infinite-scroll]');
    return {
      cards: [...doc.querySelectorAll('#product-grid > li')],
      nextUrl: next ? next.dataset.nextUrl : '',
    };
  }

  function appendCards(cards) {
    const fresh = cards.filter((li) => {
      const key = cardKey(li);
      if (key && seen.has(key)) return false;
      if (key) seen.add(key);
      return true;
    });
    grid.append(...fresh);
  }

  function afterAppend() {
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(grid);
    // New cards need the same "held in another cart" check as the first ones.
    if (typeof window.disableAddToCartFromCollectionPage === 'function') {
      window.disableAddToCartFromCollectionPage();
    }
  }

  function watchBottom() {
    // If the sentinel is still on screen, the observer won't fire again by itself.
    if (observer && nextUrl && button.hidden) {
      observer.unobserve(root);
      observer.observe(root);
    }
  }

  async function loadNext() {
    if (!nextUrl || loading) return;
    loading = true;
    root.setAttribute('aria-busy', 'true');
    button.hidden = true;
    status.hidden = false;

    try {
      const page = pageOf(nextUrl);
      const batch = await fetchBatch(nextUrl);
      appendCards(batch.cards);
      lastPage = page;
      nextUrl = batch.nextUrl;
      afterAppend();
    } catch (error) {
      console.warn('Could not load more artwork:', error);
      button.hidden = false;
    } finally {
      loading = false;
      root.removeAttribute('aria-busy');
      status.hidden = true;
    }

    if (!nextUrl) return finish();
    watchBottom();
  }

  // Coming back to the collection: bring back every batch the shopper had
  // loaded, then put them back where they were.
  // Puts a saved card back to its "checking availability" look, so the hold check
  // below decides afresh which pieces can be added to a cart.
  function resetAvailability(li) {
    li.querySelectorAll('.quick-add__submit[data-product-id]').forEach((btn) => {
      if (btn.querySelector('.sold-out-message')) return; // sold: stays sold
      btn.setAttribute('disabled', 'true');
      btn.setAttribute('data-checking-availability', 'true');
      const label = btn.querySelector('span');
      if (label) label.style.display = 'none';
      const spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    });
  }

  function doneRestoring() {
    document.documentElement.classList.remove('sbfa-restoring');
  }

  // Coming back to the collection: put back every batch the shopper had loaded,
  // at the place they were.
  async function restore(saved) {
    loading = true;
    root.setAttribute('aria-busy', 'true');

    const fresh = saved.html && Date.now() - saved.t < SNAPSHOT_MAX_AGE;

    if (fresh) {
      // From the saved copy: nothing to download, so it all appears at once.
      const template = document.createElement('template');
      template.innerHTML = saved.html;
      const cards = [...template.content.children];
      cards.forEach(resetAvailability);
      appendCards(cards);
      lastPage = saved.lastPage;
      nextUrl = saved.nextUrl;
      afterAppend();
      window.scrollTo(0, saved.scrollY);
    } else {
      status.hidden = false;
      const heading = document.querySelector('main h1, main h2');
      if (window.sbfaLoadingScreen) window.sbfaLoadingScreen.show(heading ? heading.textContent.trim() : '');
      // Hold the page open at its old height so the scroll position can be
      // restored straight away, before the batches have arrived.
      grid.style.minHeight = saved.gridHeight + 'px';
      window.scrollTo(0, saved.scrollY);

      try {
        const urls = [];
        for (let page = startPage + 1; page <= saved.lastPage && nextUrl; page++) {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('page', page);
          urls.push(url.pathname + url.search);
        }

        const results = await Promise.allSettled(urls.map(fetchBatch));
        for (let i = 0; i < results.length; i++) {
          // Stop at the first batch that failed so the grid never has a gap.
          if (results[i].status !== 'fulfilled') break;
          appendCards(results[i].value.cards);
          lastPage = pageOf(urls[i]);
          nextUrl = results[i].value.nextUrl;
        }
        afterAppend();
      } catch (error) {
        console.warn('Could not restore your place in the collection:', error);
      } finally {
        grid.style.minHeight = '';
        window.scrollTo(0, saved.scrollY);
        status.hidden = true;
        if (window.sbfaLoadingScreen) window.sbfaLoadingScreen.hide();
      }
    }

    loading = false;
    root.removeAttribute('aria-busy');
    doneRestoring();

    // Images that settle after the first paint can nudge the page; put it back once more.
    window.addEventListener('load', () => window.scrollTo(0, saved.scrollY), { once: true });

    if (!nextUrl) return finish();
    watchBottom();
  }

  button.addEventListener('click', loadNext);

  // If the browser brings this page back from its own memory (it can, on some phones),
  // what is in other carts may have changed while it was away: ask again.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && typeof window.disableAddToCartFromCollectionPage === 'function') {
      window.disableAddToCartFromCollectionPage();
    }
  });

  const navigation = performance.getEntriesByType('navigation')[0];
  const saved = readState();
  const returning = !!navigation && navigation.type === 'back_forward' && saved && saved.lastPage > startPage;

  if (returning) {
    // Take over scroll restoration so the browser doesn't jump somewhere else first.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    restore(saved);
  } else {
    doneRestoring();
    // Manual mode sticks to a history entry, so hand scrolling back to the browser.
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'auto';
    if (navigation && navigation.type !== 'back_forward') {
      try {
        sessionStorage.removeItem(STATE_KEY);
      } catch (error) {}
    }
  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadNext();
      },
      { rootMargin: '1500px 0px' }
    );
    observer.observe(root);
  } else {
    button.hidden = false;
  }
})();
